export const getSharedAudioGraph = () => {
  if (typeof window === "undefined") return null;

  const destination = window.__AI_AUDIO_DESTINATION__;
  const audioContext = window.__AI_AUDIO_CONTEXT__ || destination?.context;

  if (audioContext && destination) {
    return { audioContext, destination };
  }

  return null;
};

export const ensureSharedAudioGraph = async () => {
  const existing = getSharedAudioGraph();
  if (existing) return existing;

  const AudioContextImpl = window.AudioContext || window.webkitAudioContext;

  if (!AudioContextImpl) {
    throw new Error("Web Audio API is not available in this browser");
  }

  const audioContext = new AudioContextImpl();

  if (audioContext.state === "suspended") {
    await audioContext.resume();
  }

  const destination = audioContext.createMediaStreamDestination();

  window.__AI_AUDIO_CONTEXT__ = audioContext;
  window.__AI_AUDIO_DESTINATION__ = destination;
  window.dispatchEvent(new CustomEvent("ai-audio-graph-ready"));

  return { audioContext, destination };
};

// Track playing and scheduled audio sources.
const activePlaybacks = new Set();
let playbackGeneration = 0;

export const stopSharedAIPlayback = () => {
  playbackGeneration += 1;

  for (const playback of [...activePlaybacks]) {
    playback.cancel();
  }

  // Keep the shared recording track alive. Only audio sources stop.
};

export const playBufferThroughSharedDestination = async ({
  audioBuffer,
  localPlayback = false,
  when = 0,
  onStarted,
  onEnded,
  onError,
  cleanup,
  isPlaybackValid = () => true,
}) => {
  const generation = playbackGeneration;

  const isCurrent = () =>
    generation === playbackGeneration && isPlaybackValid();

  if (!isCurrent()) return null;

  const shared = getSharedAudioGraph();

  if (!shared) {
    onError?.(new Error("Shared audio graph unavailable"));
    return null;
  }

  const { audioContext, destination } = shared;

  try {
    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }
  } catch (error) {
    if (isCurrent()) onError?.(error);
    return null;
  }

  // Disconnect may occur while resume() is pending.
  if (!isCurrent()) return null;

  const source = audioContext.createBufferSource();
  source.buffer = audioBuffer;

  let timer = null;
  let finished = false;
  let cancelled = false;
  let started = false;

  const release = () => {
    if (finished) return;

    finished = true;
    clearTimeout(timer);
    activePlaybacks.delete(playback);

    try {
      source.disconnect();
    } catch (e) {}

    try {
      cleanup?.();
    } catch (e) {
      console.warn("[audioRouting] cleanup failed:", e);
    }
  };

  const playback = {
    cancel: () => {
      if (finished) return;

      cancelled = true;

      try {
        source.stop();
      } catch (e) {}

      release();
    },
  };

  source.onended = () => {
    const notify = !finished && !cancelled && isCurrent();

    release();

    if (notify) onEnded?.();
  };

  source.onerror = () => {
    const notify = !finished && !cancelled && isCurrent();

    release();

    if (notify) {
      onError?.(new Error("Audio source error"));
    }
  };

  try {
    source.connect(destination);

    if (localPlayback) {
      source.connect(audioContext.destination);
    }

    activePlaybacks.add(playback);

    const startAt = Math.max(when, audioContext.currentTime);
    source.start(startAt);

    const notifyStarted = () => {
      if (finished || cancelled || started) return;

      if (!isCurrent()) {
        playback.cancel();
        return;
      }

      // Wait for the AudioContext clock, including when it is suspended.
      const remaining = (startAt - audioContext.currentTime) * 1000;

      if (audioContext.state !== "running" || remaining > 1) {
        timer = setTimeout(
          notifyStarted,
          Math.max(10, Math.min(remaining || 25, 100)),
        );
        return;
      }

      started = true;

      try {
        onStarted?.();
      } catch (e) {
        console.warn("[audioRouting] onStarted callback failed:", e);
      }
    };

    timer = setTimeout(
      notifyStarted,
      Math.max(0, (startAt - audioContext.currentTime) * 1000),
    );

    return source;
  } catch (error) {
    playback.cancel();

    if (isCurrent()) onError?.(error);

    return null;
  }
};